import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import OptionsMenu from "./optionsMenu";

import folderIcon from "../../assets/icons/folder.svg";
import subfolderIcon from "../../assets/icons/subfolder.svg";
import arrowDownIcon from "../../assets/icons/arrow_right.svg";
import campaignIcon from "../../assets/icons/campaign.svg";
import folderCheckIcon from "../../assets/icons/folder_check.svg";
import {
  RowType,
  ParentTree,
  AttributesTableProps,
  SegmentNode,
  SegmentRowProps,
} from "./segment-interface";
import dayjs from "dayjs";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

function flattenSegments(
  parents: ParentTree[],
  level: number = 0,
  parentKey: string = ""
): RowType[] {
  const rows: RowType[] = [];
  try {
    parents.forEach((parent, parentIndex) => {
      const parentName = parent.folder1?.trim() || `Folder ${parentIndex + 1}`;
      const parentKeyName = `${parentName}-${parentIndex}`;
      rows.push({
        key: parentKeyName,
        name: parentName,
        status: "",
        lastEdited: "",
        market: parentName,
        usedInCampaign: "",
        consumer: "",
        level: 0,
        isFolder: true,
        isOpen: false,
        children: parent.children
          ? flattenChildren(parent.children, 1, parentKeyName, parent.folder1)
          : [],
      });
    });
  } catch (error) {
    console.error("Error flattening segments:", error);
  }
  return rows;
}

function flattenChildren(
  nodes: SegmentNode[],
  level: number,
  parentKey: string,
  market: string
): RowType[] {
  return nodes.map((node, index) => {
    const key = `${parentKey}-${node.name}-${index}`;
    const isFolder = node.type === "folder";

    return {
      key,
      name: node.name,
      status: node.status || "-",
      lastEdited: node.last_modified ? dayjs(node.last_modified).format("MMM DD, YYYY") : "-",
      market,
      usedInCampaign: node.campaigns?.join(", ") || "-",
      consumer: node.count ? String(node.count) : "-",
      level,
      isFolder,
      isOpen: false,
      id: node?.id,
      children: node.children?.length ? flattenChildren(node.children, level + 1, key, market) : [],
    };
  });
}

function SegmentRow({ row, isOpen, toggleOpen, countryAdminMap }: SegmentRowProps) {
  const { createLink } = useSimulationParams();
  const indent = row.level * 24;

  const handleRowClick = () => {
    if (row.isFolder) {
      toggleOpen(row.key);
    }
  };

  let icon = null;
  if (row.isFolder || row.level === 0) {
    icon =
      row.level === 0 ? (
        <img src={folderIcon} alt="Folder" style={{ width: 20, height: 20, marginRight: 8 }} />
      ) : (
        <img
          src={subfolderIcon}
          alt="Subfolder"
          style={{ width: 20, height: 20, marginRight: 8 }}
        />
      );
  }

  const arrowIcon = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        toggleOpen(row.key);
      }}
      sx={{ p: 0.5, mr: 0.5 }}
    >
      <img
        src={arrowDownIcon}
        alt={isOpen ? "Collapse" : "Expand"}
        style={{
          width: 16,
          height: 16,
          transform: isOpen ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );

  return (
    <TableRow
      hover
      component={!row.isFolder ? Link : "tr"}
      {...(!row.isFolder
        ? { to: createLink(`/segments/${encodeURIComponent(row?.id)}`) }
        : {})}
      onClick={row.isFolder ? handleRowClick : undefined}
      style={{ cursor: "pointer", textDecoration: "none", color: "inherit" }}
    >
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", pl: `${indent}px` }}>
          {row.isFolder ? arrowIcon : <Box sx={{ width: 32 }} />}
          {icon}
          <Typography
            variant="h6"
            sx={{
              fontSize: "calc(14 / var(--base-font) * 1rem)",
              fontWeight: row.level === 0 ? 700 : row.level === 3 ? 500 : 400,
              color: row.level === 0 ? "#e6007a" : "#222",
            }}
          >
            {row.name}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {!row.isFolder && (
            <>
              <img
                src={row.status.toLowerCase().replace(/\s+/g, "") === "inuse" ? campaignIcon : folderCheckIcon}
                alt="{row.status}"
                style={{ width: 16, height: 16, marginRight: 4 }}
              />
              <span>{row.status}</span>
            </>
          )}
        </Box>
      </TableCell>
      <TableCell>{row.lastEdited || "-"}</TableCell>
      <TableCell
        sx={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.usedInCampaign}
      </TableCell>
      <TableCell>{row.consumer}</TableCell>
      <TableCell
        className="cell-actions"
        key="options-menu"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          textAlign: "right",
          cursor: "default",
          "&:hover": { backgroundColor: "#f5f5f5" },
          "&:focus": { backgroundColor: "#f5f5f5" },
          "&:active": { backgroundColor: "#e0e0e0" },
        }}
      >
        {row.isFolder || row.level === 0 ? null : (
          <Box>
            {countryAdminMap &&
            Object.keys(countryAdminMap).some(
              (country) => country.toLowerCase() === row.market?.toLowerCase()
            ) ? (
              <OptionsMenu pathname="segments" usedInCampaign={row?.usedInCampaign} id={row?.id} name={row?.name} />
            ) : null}
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
}

function toggleIsOpen(rows: RowType[], key: string): RowType[] {
  return rows.map((row) => {
    if (row.key === key) {
      return { ...row, isOpen: !row.isOpen };
    }
    if (row.children) {
      return { ...row, children: toggleIsOpen(row.children, key) };
    }
    return row;
  });
}

function SegmentsTableTree({ data, countryAdminMap, stickyTop}: AttributesTableProps) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));

  const getColumnWidths = () => {
    if (isXs) {
      return {
        name: "30%",
        status: "12%",
        lastEdited: "13%",
        market: "10%",
        campaign: "35%",
        consumer: "10%"
      };
    }
    if (isSm) {
      return {
        name: "28%",
        status: "12%",
        lastEdited: "15%",
        market: "10%",
        campaign: "35%",
        consumer: "10%"
      };
    }
    if (isMd) {
      return {
        name: "24%",
        status: "13%",
        lastEdited: "15%",
        market: "10%",
        campaign: "35%",
        consumer: "10%"
      };
    }
    if (isLg) {
      return {
        name: "40%",
        status: "10%",
        lastEdited: "10%",
        market: "10%",
        campaign: "35%",
        consumer: "10%"
      };
    }
    return {
      name: "20%",
      status: "13%",
      lastEdited: "15%",
      market: "10%",
      campaign: "35%",
      consumer: "10%"
    };
  };

  const colWidths = getColumnWidths();

  const [rows, setRows] = useState<RowType[]>([]);

  const [allExpanded, setAllExpanded] = useState(false);

  function setAllRowsOpen(rows: RowType[], open: boolean): RowType[] {
    return rows.map((row) => ({
      ...row,
      isOpen: open,
      children: row.children ? setAllRowsOpen(row.children, open) : [],
    }));
  }

  const arrowIconHeader = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        const newState = !allExpanded;
        setAllExpanded(newState);

        // Update openMap for consistency
        const updatedMap: Record<string, boolean> = {};
        const updateMap = (rows: RowType[]) => {
          rows.forEach((row) => {
            if (row.children && row.children.length) {
              updatedMap[row.key] = newState;
              updateMap(row.children);
            }
          });
        };
        updateMap(rows);

        setOpenMap(updatedMap);
        setRows((prevRows) => setAllRowsOpen(prevRows, newState));
      }}
      sx={{ p: 0.5, mr: 0.5 }}
    >
      <img
        src={arrowDownIcon}
        alt={allExpanded ? "Collapse all" : "Expand all"}
        style={{
          width: 16,
          height: 16,
          transform: allExpanded ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );

  useEffect(() => {
    if (Array.isArray(data)) {
      setRows(flattenSegments(data as ParentTree[]));
    }
  }, [data]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const toggleOpen = (key: string) => {
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    setRows((prevRows) => toggleIsOpen(prevRows, key));
  };

  const renderRows = (rows: RowType[]): React.ReactElement[] => {
    return rows.flatMap((row) => {
      const rowElement = (
        <SegmentRow
          key={row.key}
          row={row}
          isOpen={!!openMap[row.key]}
          toggleOpen={toggleOpen}
          countryAdminMap={countryAdminMap}
        />
      );
      if (row.isFolder && openMap[row.key] && row.children) {
        return [rowElement, ...renderRows(row.children)];
      }
      return [rowElement];
    });
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table sx={{ tableLayout: "fixed" }}>
        <TableHead sx={{ "--table-sticky-top": `${stickyTop}px` } as React.CSSProperties}>
          <TableRow>
            <TableCell className="cell-name" sx={{ width: colWidths.name }}>
              {arrowIconHeader} Name
            </TableCell>
            <TableCell className="cell-status" sx={{ width: colWidths.status}}>
              Status
            </TableCell>
            <TableCell
              className="cell-last_edited"
              sx={{ width: colWidths.lastEdited }}
            >
              Last edited
            </TableCell>
            <TableCell className="cell-campaign" sx={{ width: colWidths.campaign }}>
              Used in campaign
            </TableCell>
            <TableCell className="cell-consumer" sx={{ width: colWidths.consumer}}>
              #consumer
            </TableCell>
            <TableCell
              className="cell-actions"
            />
          </TableRow>
        </TableHead>
        <TableBody>{renderRows(rows)}</TableBody>
      </Table>
    </TableContainer>
  );
}

export default SegmentsTableTree;
